import { z as encodeFillPatternColorToken } from "./doenetml-drNYjJiF.js";
const warnedUnsupportedFillStyles = /* @__PURE__ */ new Set();
function warnUnsupportedFillStyle(fillStyle) {
  if (warnedUnsupportedFillStyles.has(fillStyle)) {
    return;
  }
  warnedUnsupportedFillStyles.add(fillStyle);
  console.warn(
    `DoenetML: unsupported fillStyle "${fillStyle}" rendered as a solid fill.`
  );
}
const FILL_PATTERN_DEFS = {
  horizontal: { width: 8, height: 8, path: "M0,4 L8,4" },
  vertical: { width: 8, height: 8, path: "M4,0 L4,8" },
  // Split into two half-segments so endpoints land at tile-edge midpoints,
  // not corners, which prevents the corner anti-aliasing gap on tiling.
  // Round linecap + overflow="visible" on the pattern element let each
  // endpoint's cap bleed slightly across the tile boundary so adjacent
  // tiles' strokes meet without a sub-pixel seam.
  diagonal: {
    width: 12,
    height: 12,
    path: "M0,6 L6,0 M6,12 L12,6",
    strokeLinecap: "round"
  },
  backdiagonal: {
    width: 12,
    height: 12,
    path: "M0,6 L6,12 M6,0 L12,6",
    strokeLinecap: "round"
  },
  // Hex grid: tile 18×31 (≈ 18 × 18√3).  Two fully interior dots so every
  // dot is rendered the same size (no partial-circle corner assembly).
  dots: {
    width: 18,
    height: 31,
    path: "M4.5,7.75 L4.5,7.75 M13.5,23.25 L13.5,23.25",
    strokeWidth: 4,
    strokeLinecap: "round"
  },
  // Hex grid: tile 12×21 (≈ 12 × 12√3).  Center rhombus + four corner
  // quarter-triangles that assemble into a second rhombus when four tiles
  // meet.  Horizontal half-extent 3, vertical half-extent 5 (≈ 3√3).
  // Tile width = 4a = 12 so the gap between diamonds (6 px) approximately
  // equals the diamond width (6 px).
  diamonds: {
    width: 12,
    height: 21,
    path: "M6,5.5 L9,10.5 L6,15.5 L3,10.5 Z  M0,0 L3,0 L0,5 Z  M12,0 L9,0 L12,5 Z  M0,21 L3,21 L0,16 Z  M12,21 L9,21 L12,16 Z",
    useFill: true
  }
};
function encodeOpacityToken(value) {
  return String(value).replace(/[^0-9]/g, "_");
}
function buildPatternId({
  boardId,
  fillStyle,
  fillColor,
  canvasColor,
  fillOpacity,
  fillPatternOpacity
}) {
  return [
    "doenet-fill-pattern",
    boardId,
    fillStyle,
    encodeFillPatternColorToken(fillColor),
    encodeFillPatternColorToken(canvasColor),
    encodeOpacityToken(fillOpacity),
    encodeOpacityToken(fillPatternOpacity)
  ].join("-");
}
function getOrInjectPattern({
  defsEl,
  boardId,
  fillStyle,
  fillColor,
  fillOpacity,
  canvasColor,
  fillPatternOpacity
}) {
  const def = FILL_PATTERN_DEFS[fillStyle];
  if (!def) {
    if (fillStyle !== "solid") {
      warnUnsupportedFillStyle(fillStyle);
    }
    return fillColor;
  }
  if (!defsEl) {
    return fillColor;
  }
  const id = buildPatternId({
    boardId,
    fillStyle,
    fillColor,
    fillOpacity,
    canvasColor,
    fillPatternOpacity
  });
  if (defsEl.ownerDocument?.getElementById(id)) {
    return `url(#${id})`;
  }
  const svgNS = "http://www.w3.org/2000/svg";
  const ownerDocument = defsEl.ownerDocument ?? document;
  const pattern = ownerDocument.createElementNS(svgNS, "pattern");
  pattern.setAttribute("id", id);
  pattern.setAttribute("width", String(def.width));
  pattern.setAttribute("height", String(def.height));
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  pattern.setAttribute("overflow", "visible");
  const background = ownerDocument.createElementNS(svgNS, "rect");
  background.setAttribute("width", String(def.width));
  background.setAttribute("height", String(def.height));
  background.setAttribute("fill", canvasColor);
  background.setAttribute("fill-opacity", String(fillOpacity));
  pattern.appendChild(background);
  const path = ownerDocument.createElementNS(svgNS, "path");
  path.setAttribute("d", def.path);
  if (def.useFill) {
    path.setAttribute("fill", fillColor);
    path.setAttribute("fill-opacity", String(fillPatternOpacity));
    path.setAttribute("stroke", "none");
  } else {
    path.setAttribute("stroke", fillColor);
    path.setAttribute("stroke-width", String(def.strokeWidth ?? 1.5));
    path.setAttribute("stroke-opacity", String(fillPatternOpacity));
    path.setAttribute("fill", "none");
    if (def.strokeLinecap) {
      path.setAttribute("stroke-linecap", def.strokeLinecap);
    }
  }
  pattern.appendChild(path);
  defsEl.appendChild(pattern);
  return `url(#${id})`;
}
function getPatternFillAttributes({
  defsEl,
  boardId,
  fillStyle,
  fillColor,
  fillOpacity,
  canvasColor,
  fillPatternOpacity = 1,
  highlightFillOpacity = fillOpacity * 0.5
}) {
  const resolvedFillColor = getOrInjectPattern({
    defsEl,
    boardId,
    fillStyle,
    fillColor,
    fillOpacity,
    canvasColor,
    fillPatternOpacity
  });
  const usesPatternFill = resolvedFillColor !== fillColor;
  return {
    fillColor: resolvedFillColor,
    fillOpacity: usesPatternFill ? 1 : fillOpacity,
    highlightFillColor: resolvedFillColor,
    highlightFillOpacity: usesPatternFill ? 0.5 : highlightFillOpacity
  };
}
export {
  getPatternFillAttributes as g
};
